import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import SearchIcon from "../../assets/icons/SearchIcon";
import { searchOpportunitiesAPI } from "../../utils/requests/opportunitySearch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faMapMarkerAlt, faBuilding } from "@fortawesome/free-solid-svg-icons";
import "./OpportunitySearch.css";

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;
const SEARCH_PREVIEW_LIMIT = 20;

const STATUS_LABELS = { A: "Active", NA: "Historical" };

function StatusBadge({ status }) {
  if (!status || !STATUS_LABELS[status]) return null;
  const isActive = status === "A";
  return (
    <span
      className={`opportunity-search-status-badge ${
        isActive ? "opportunity-search-status-badge--active" : "opportunity-search-status-badge--historical"
      }`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

export function OpportunityResultItem({ opp, onSelect, variant, isFocused }) {
  const ref = useRef(null);
  useEffect(() => {
    if (isFocused && ref.current) ref.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [isFocused]);

  const locationLine = [opp.address, opp.city, opp.state_short].filter(Boolean).join(", ");
  const metaLine = [opp.account_name, opp.project_type, opp.sf_size ? `${opp.sf_size} sq ft` : null].filter(Boolean).join(" · ");

  if (variant === "fullPage") {
    return (
      <button
        ref={ref}
        type="button"
        className={`opportunity-search-result-card ${isFocused ? "opportunity-search-result-card--focused" : ""}`}
        onClick={() => onSelect(opp)}
      >
        <div className="opportunity-search-result-card__name">
          <span>{opp.name || "Unnamed"}</span>
          <StatusBadge status={opp.status} />
        </div>
        {locationLine && (
          <div className="opportunity-search-result-card__location">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="opportunity-search-result-card__icon" />
            {locationLine}
          </div>
        )}
        {metaLine && (
          <div className="opportunity-search-result-card__meta">
            <FontAwesomeIcon icon={faBuilding} className="opportunity-search-result-card__icon" />
            {metaLine}
          </div>
        )}
      </button>
    );
  }

  return (
    <li ref={ref}>
      <button
        type="button"
        className={`opportunity-search-result-inline ${isFocused ? "opportunity-search-result-inline--focused" : ""}`}
        onClick={() => onSelect(opp)}
      >
        <div className="opportunity-search-result-inline__content">
          <div className="opportunity-search-result-inline__name">
            <span>{opp.name || "Unnamed"}</span>
            <StatusBadge status={opp.status} />
          </div>
          {locationLine && (
            <div className="opportunity-search-result-inline__location">
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              <span>{locationLine}</span>
            </div>
          )}
          {metaLine && (
            <div className="opportunity-search-result-inline__meta">
              <FontAwesomeIcon icon={faBuilding} />
              <span>{metaLine}</span>
            </div>
          )}
        </div>
      </button>
    </li>
  );
}

export function OpportunitySearch({
  variant = "inline",
  placeholder = "Search opportunities...",
  onSelect: onSelectProp,
  containerRef,
  className = "",
  inputClassName = "",
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  const onSelect = useCallback(
    (opp) => {
      const path = `/project_board/${opp.url_slug}`;
      if (onSelectProp) onSelectProp(opp);
      else window.open(path, "_blank", "noopener,noreferrer");
      // setQuery("");
      // setResults([]);
      setShowDropdown(false);
      setFocusedIndex(-1);
    },
    [onSelectProp]
  );

  useEffect(() => {
    if (!query || query.trim().length < MIN_QUERY_LENGTH) {
      setResults([]);
      setTotalCount(0);
      setShowDropdown(!!query.trim());
      setFocusedIndex(-1);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      searchOpportunitiesAPI(query, { page: 1, page_size: SEARCH_PREVIEW_LIMIT })
        .then((data) => {
          setResults(data.results || []);
          setTotalCount(typeof data.count === "number" ? data.count : (data.results || []).length);
          setShowDropdown(true);
          setFocusedIndex(-1);
        })
        .catch(() => {
          setResults([]);
          setTotalCount(0);
        })
        .finally(() => setLoading(false));
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  useEffect(() => {
    setFocusedIndex((i) => (i >= results.length ? results.length - 1 : i));
  }, [results.length]);

  const handleKeyDown = (e) => {
    if (!showDropdown || results.length === 0) {
      if (e.key === "Escape") setShowDropdown(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((i) => (i < results.length - 1 ? i + 1 : i));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((i) => (i > 0 ? i - 1 : -1));
    } else if (e.key === "Enter" && focusedIndex >= 0 && results[focusedIndex]) {
      e.preventDefault();
      onSelect(results[focusedIndex]);
    } else if (e.key === "Escape") {
      setShowDropdown(false);
      setFocusedIndex(-1);
    }
  };

  const showResults = showDropdown && (query.trim().length >= MIN_QUERY_LENGTH || query.trim().length > 0);

  return (
    <div
      ref={containerRef}
      className={`opportunity-search opportunity-search--${variant} ${className}`}
    >
      <div className="opportunity-search-input-wrap">
        <input
          ref={inputRef}
          type="text"
          className={`opportunity-search-input ${inputClassName}`}
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => (query.trim().length >= MIN_QUERY_LENGTH ? setShowDropdown(true) : setShowDropdown(!!query.trim()))}
          onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
          onKeyDown={handleKeyDown}
          aria-label="Search opportunities"
          // aria-expanded={showResults}
          aria-autocomplete="list"
        />
        {query && (
          <button
            type="button"
            className="opportunity-search-clear"
            onClick={() => {
              setQuery("");
              setResults([]);
              setTotalCount(0);
              setShowDropdown(false);
              setFocusedIndex(-1);
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
        <SearchIcon
          fill="#6c757d"
          width={variant === "fullPage" ? 22 : 18}
          height={variant === "fullPage" ? 22 : 18}
          className="opportunity-search-icon"
          aria-hidden
        />
      </div>

      {variant === "inline" && showResults && (
        <div className="opportunity-search-dropdown">
          {loading ? (
            <div className="opportunity-search-dropdown-loading">
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
              <span>Searching...</span>
            </div>
          ) : query.trim().length < MIN_QUERY_LENGTH ? (
            <div className="opportunity-search-dropdown-hint">Type at least {MIN_QUERY_LENGTH} characters</div>
          ) : results.length === 0 ? (
            <div className="opportunity-search-dropdown-hint">No opportunities found. Try name, address, location, company name, or building type.</div>
          ) : (
            <>
              <div className="opportunity-search-dropdown-header">
                {totalCount > SEARCH_PREVIEW_LIMIT
                  ? `Showing ${results.length} of ${totalCount} results`
                  : `${totalCount} ${totalCount === 1 ? "result" : "results"}`}
              </div>
              <ul className="opportunity-search-results-inline list-unstyled mb-0">
              {results.map((opp, i) => (
                <OpportunityResultItem
                  key={opp.id}
                  opp={opp}
                  onSelect={onSelect}
                  variant="inline"
                  isFocused={i === focusedIndex}
                />
              ))}
              </ul>
              {totalCount > SEARCH_PREVIEW_LIMIT && (
                <div className="opportunity-search-view-more-wrap">
                  <Link
                    to={`/search/results?q=${encodeURIComponent(query.trim())}`}
                    className="opportunity-search-view-more"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setShowDropdown(false)}
                  >
                    View more results
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {variant === "fullPage" && (
        <div className="opportunity-search-full-results">
          {loading && (
            <div className="opportunity-search-full-loading">
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
              <span>Searching...</span>
            </div>
          )}
          {!loading && query.trim().length > 0 && query.trim().length < MIN_QUERY_LENGTH && (
            <p className="opportunity-search-full-hint">Type at least {MIN_QUERY_LENGTH} characters to search.</p>
          )}
          {!loading && query.trim().length >= MIN_QUERY_LENGTH && results.length === 0 && (
            <p className="opportunity-search-full-hint">No opportunities found. Try name, address, location, company name, or building type.</p>
          )}
          {!loading && results.length > 0 && (
            <>
              {totalCount > SEARCH_PREVIEW_LIMIT && (
                <div className="opportunity-search-full-summary mb-2">
                  <p className="opportunity-search-full-count-hint mb-0">
                    Showing {results.length} of {totalCount} results
                  </p>
                  <Link
                    to={`/search/results?q=${encodeURIComponent(query.trim())}`}
                    className="opportunity-search-view-more opportunity-search-view-more--inline"
                  >
                    View more results
                  </Link>
                </div>
              )}
              <div className="opportunity-search-results-full">
                {results.map((opp, i) => (
                  <OpportunityResultItem
                    key={opp.id}
                    opp={opp}
                    onSelect={onSelect}
                    variant="fullPage"
                    isFocused={i === focusedIndex}
                  />
                ))}
              </div>
              {totalCount > SEARCH_PREVIEW_LIMIT && (
                <div className="opportunity-search-view-more-wrap opportunity-search-view-more-wrap--full">
                  <Link
                    to={`/search/results?q=${encodeURIComponent(query.trim())}`}
                    className="opportunity-search-view-more"
                  >
                    View more results
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default OpportunitySearch;
